import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { confirmDialog, toast } from '../../../components/notifications/notificationService';
import {
  Building2,
  Eye,
  FileClock,
  IndianRupee,
  Pencil,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  X
} from 'lucide-react';
import { isAuthenticated, getUserRole, getUserPrivileges } from '../../../utils/auth';
import api from '../../../utils/api';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './DraftPolicies.css';
import { API_BASE_URL } from '../../../utils/apiBase';

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const parseAmount = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

const DraftPolicies = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('');
  const [viewDraft, setViewDraft] = useState(null);
  const navigate = useNavigate();
  const isAdmin = getUserRole() === 'admin';
  const canDeleteDraft = isAdmin || getUserPrivileges().includes('transactions.draftPolicyDelete');

  const loadDrafts = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await api.get(`${API_BASE_URL}/api/add-policy/drafts`);
      setDrafts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load draft policies.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  const filteredDrafts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return drafts.filter((draft) =>
      (!clientTypeFilter || draft.clientType === clientTypeFilter) &&
      (!normalizedSearch || [
        draft.clientName,
        draft.policyNumber,
        draft.insurerName,
        draft.policyName,
        draft.clientType,
        draft.ownerId?.name,
        draft.ownerId?.username
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch))
    );
  }, [drafts, searchTerm, clientTypeFilter]);

  const clientTypeOptions = useMemo(
    () => [...new Set(drafts.map((draft) => draft.clientType).filter(Boolean))].sort(),
    [drafts]
  );

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);

  const summary = useMemo(() => {
    const premium = drafts.reduce((sum, draft) => sum + parseAmount(draft.grossPremium), 0);
    const owners = new Set(drafts.map((draft) => draft.ownerId?._id || draft.ownerId?.username).filter(Boolean));
    const noPolicyNumber = drafts.filter((draft) => !draft.policyNumber).length;

    return {
      total: drafts.length,
      filtered: filteredDrafts.length,
      premium,
      owners: owners.size,
      noPolicyNumber
    };
  }, [drafts, filteredDrafts]);

  const summaryCards = [
    { label: 'Total Drafts', value: summary.total, note: `${summary.filtered} currently shown`, icon: FileClock, tone: 'is-blue' },
    { label: 'Draft Premium', value: formatCurrency(summary.premium), note: 'Gross premium in drafts', icon: IndianRupee, tone: 'is-green' },
    { label: 'Client Types', value: clientTypeOptions.length, note: 'Available draft segments', icon: UserRound, tone: 'is-amber' },
    { label: 'Owners', value: summary.owners || (isAdmin ? 0 : 1), note: isAdmin ? 'Draft creators' : 'Your draft workspace', icon: ShieldCheck, tone: 'is-teal' },
    { label: 'No Policy No.', value: summary.noPolicyNumber, note: 'Still missing number', icon: Building2, tone: 'is-indigo' }
  ];

  const resetFilters = () => {
    setSearchTerm('');
    setClientTypeFilter('');
  };

  const continueDraft = (draft) => {
    navigate('/add-policy', {
      state: {
        draftId: draft._id
      }
    });
  };

  const deleteDraft = async (draft) => {
    const confirmed = await confirmDialog({ title: 'Delete draft policy?', message: `This will permanently delete ${draft.clientName || 'this draft policy'}.`, confirmText: 'Delete Draft' });
    if (!confirmed) return;

    try {
      await api.delete(`${API_BASE_URL}/api/add-policy/drafts/${draft._id}`);
      setDrafts((current) => current.filter((item) => item._id !== draft._id));
      if (viewDraft?._id === draft._id) {
        setViewDraft(null);
      }
      toast.success('Draft policy deleted successfully.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete draft policy.', { title: 'Delete failed' });
    }
  };

  if (!isAuthenticated()) {
    return <Navigate to="/" />;
  }

  return (
    <div className="draft-policies-page">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      <div className={`draft-policies-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="draft-policies-sticky-header">
          <Header />
        </div>

        <div className="draft-policies-scroll-area">
          <section className="draft-policies-shell">
            <div className="draft-page-hero">
              <div>
                <span className="draft-eyebrow"><FileClock size={15} /> Policy Transactions</span>
                <h1>Draft Policies</h1>
                <p>Review, edit, and continue incomplete policy entries.</p>
              </div>
              <div className="draft-hero-stats">
                <span>Total Drafts</span>
                <strong>{summary.total}</strong>
                <small>{summary.filtered} visible</small>
              </div>
            </div>

            <div className="draft-summary-grid">
              {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
                <div className={`draft-summary-card ${tone}`} key={label}>
                  <Icon size={22} />
                  <span>{label}</span>
                  <strong>{value}</strong>
                  <small>{note}</small>
                </div>
              ))}
            </div>

            {error && <div className="draft-alert">{error}</div>}

            <div className="draft-filter-card">
              <div className="draft-filter-header">
                <div>
                  <h2>Report Filters</h2>
                  <p>Search draft transactions by client, policy, insurer, or owner.</p>
                </div>
                <span className="draft-result-pill">{filteredDrafts.length} results</span>
              </div>

              <div className="draft-filter-grid">
                <label>
                  <span>Search</span>
                  <div className="draft-search-input">
                    <Search size={17} />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Client, policy, insurer, owner"
                    />
                  </div>
                </label>

                <label>
                  <span>Client Type</span>
                  <select value={clientTypeFilter} onChange={(e) => setClientTypeFilter(e.target.value)}>
                    <option value="">All Clients</option>
                    {clientTypeOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="draft-action-row">
                <button type="button" className="draft-btn draft-btn-primary" onClick={loadDrafts} disabled={loading}>
                  <RefreshCw size={17} className={loading ? 'draft-spin' : ''} />
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
                <button type="button" className="draft-btn draft-btn-light" onClick={resetFilters} disabled={!searchTerm && !clientTypeFilter}>
                  <X size={17} />
                  Reset Filters
                </button>
              </div>
            </div>

            <div className="draft-table-card">
              <div className="draft-table-toolbar">
                <div>
                  <h2>Draft Policy Details</h2>
                  <p>{filteredDrafts.length} rows matched from {drafts.length} loaded drafts</p>
                </div>
                <span><FileClock size={15} /> Saved draft register</span>
              </div>

              <div className="draft-table-wrap">
                <table className="draft-table">
                  <thead>
                    <tr>
                      <th className="draft-sticky-client">Client</th>
                      <th>Policy Number</th>
                      <th>Policy Name</th>
                      <th>Insurer</th>
                      <th>Status</th>
                      <th>Saved At</th>
                      {isAdmin && <th>Owner</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={isAdmin ? 8 : 7} className="draft-empty">
                          Loading draft policies...
                        </td>
                      </tr>
                    ) : filteredDrafts.length ? (
                      filteredDrafts.map((draft) => (
                        <tr key={draft._id}>
                          <td className="draft-sticky-client draft-client-cell">
                            <strong>{draft.clientName || '-'}</strong>
                            <span>{draft.clientType || '-'}</span>
                          </td>
                          <td>{draft.policyNumber || '-'}</td>
                          <td>{draft.policyName || draft.classOfPolicyName || '-'}</td>
                          <td>{draft.insurerName || '-'}</td>
                          <td>
                            <span className="draft-status-badge">Draft</span>
                          </td>
                          <td>{formatDate(draft.draftSavedAt || draft.createdAt)}</td>
                          {isAdmin && (
                            <td>
                              <strong>{draft.ownerId?.name || draft.ownerId?.username || '-'}</strong>
                              <span>{draft.ownerId?.employeeId || '-'}</span>
                            </td>
                          )}
                          <td>
                            <div className="draft-action-group">
                              <button type="button" className="draft-icon-btn neutral" title="View" aria-label="View" onClick={() => setViewDraft(draft)}>
                                <Eye size={16} />
                              </button>
                              <button type="button" className="draft-icon-btn primary" title="Continue" aria-label="Continue" onClick={() => continueDraft(draft)}>
                                <Pencil size={16} />
                              </button>
                              {canDeleteDraft && (
                                <button type="button" className="draft-icon-btn danger" title="Delete" aria-label="Delete" onClick={() => deleteDraft(draft)}>
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={isAdmin ? 8 : 7} className="draft-empty">
                          No draft policies found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </div>

      {viewDraft && (
        <div className="draft-view-overlay" onClick={() => setViewDraft(null)}>
          <div className="draft-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="draft-view-head">
              <div>
                <h3>Draft Policy Details</h3>
                <p>{viewDraft.clientName || '-'} - {viewDraft.policyNumber || 'No policy number yet'}</p>
              </div>
              <button type="button" onClick={() => setViewDraft(null)}>
                &times;
              </button>
            </div>

            <div className="draft-view-grid">
              <p><strong>Client Type:</strong> {viewDraft.clientType || '-'}</p>
              <p><strong>Client Name:</strong> {viewDraft.clientName || '-'}</p>
              <p><strong>Policy Number:</strong> {viewDraft.policyNumber || '-'}</p>
              <p><strong>Policy Name:</strong> {viewDraft.policyName || '-'}</p>
              <p><strong>Class of Policy:</strong> {viewDraft.classOfPolicyName || '-'}</p>
              <p><strong>Insurer:</strong> {viewDraft.insurerName || '-'}</p>
              <p><strong>Branch:</strong> {viewDraft.branchName || '-'}</p>
              <p><strong>Business Type:</strong> {viewDraft.businessType || '-'}</p>
              <p><strong>Gross Premium:</strong> {viewDraft.grossPremium ?? '-'}</p>
              <p><strong>Saved At:</strong> {formatDate(viewDraft.draftSavedAt || viewDraft.createdAt)}</p>
            </div>

            <div className="draft-view-actions">
              <button type="button" className="draft-view-btn" onClick={() => setViewDraft(null)}>
                Close
              </button>
              <button type="button" className="draft-continue-btn" onClick={() => continueDraft(viewDraft)}>
                Continue Editing
              </button>
              {canDeleteDraft && (
                <button type="button" className="draft-delete-btn" onClick={() => deleteDraft(viewDraft)}>
                  Delete Draft
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftPolicies;
